import { Address, COMPONENT_TYPE, INPUT_COMPONENT_TYPE } from '@abiglobalhealth/go-types'
import { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { setFulfillmentDelivery } from '@/api/delivery'
import { Button } from '@/components/Button'
import { Input } from '@/components/Input'
import { useConsultation } from '@/hooks/useConsultation'
import { useFormErrors } from '@/hooks/useFormErrors'
import { useLoading } from '@/hooks/useLoading'
import { useUser } from '@/hooks/useUser'
import { requiredString } from '@/utils/zod'

const StyledInput = ({ id, label, placeholder }: { id: string; label: string; placeholder: string }) => {
  return (
    <Input componentType={COMPONENT_TYPE.INPUT} id={id} tabIndex={0} type={INPUT_COMPONENT_TYPE.TEXT} textKey={label} placeholder={placeholder} />
  )
}

const zodSchema = z.object({
  house: requiredString,
  building: requiredString,
  street: requiredString,
  district: requiredString
})

export const DeliveryForm = () => {
  const { t } = useTranslation()
  const { showLoading, hideLoading } = useLoading()
  const { user } = useUser()
  const { addFormError } = useFormErrors()
  const { updateConsultation } = useConsultation()

  const handleConfirm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)
    try {
      zodSchema.parse({
        house: formData.get('house'),
        street: formData.get('street'),
        building: formData.get('building'),
        district: formData.get('district')
      })
    } catch (err) {
      if (err instanceof z.ZodError) {
        for (const error of err.issues) {
          addFormError(error.path.join('.'), error.message)
        }
      } else {
        console.error(err)
      }

      return
    }

    const address: Record<string, string> = {
      country: t('country.hk')
    }
    for (const [id, value] of formData.entries()) {
      address[id] = value as string
    }

    showLoading()

    const consultation = await setFulfillmentDelivery({
      uniqueId: user?.data?.uniqueId!,
      address: address as Address
    })
    updateConsultation(consultation)

    hideLoading()
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <p className="text-primary text-center text-2xl font-bold">{t('delivery.address.title')}</p>
      <p>{t('delivery.address.description')}</p>

      <form id="delivery-form" onSubmit={handleConfirm}>
        <div className="flex flex-col py-4">
          <StyledInput id="house" label="delivery.address.number_and_floor" placeholder="delivery.address.number_and_floor.placeholder" />
          <StyledInput id="building" label="delivery.address.building_name_number" placeholder="delivery.address.building_name_number.placeholder" />
          <StyledInput id="street" label="delivery.address.street" placeholder="delivery.address.street.placeholder" />
          <StyledInput id="district" label="delivery.address.district_city" placeholder="delivery.address.district_city.placeholder" />
        </div>

        <Button>{t('button.confirm')}</Button>
      </form>
    </div>
  )
}
